import { jsxs, jsx } from 'react/jsx-runtime';
import { useState } from 'react';
import { useFloating, autoUpdate, useTransitionStyles, useDismiss, useClick, useHover, safePolygon, useInteractions, FloatingPortal, offset, shift, limitShift, hide, flip } from '@floating-ui/react';
import { EmptyInfoCircleIcon } from '@ninja/icons/web';

/** The tooltips to show extra content when hovering or focusing on an element */
const Tooltip = ({ direction = 'top', childElement, parentElement = 'icon', className, variant = 'default', isOpen, onIsOpenChange, persistOnClick = false, isFlippable = true, removeParentGap = false, }) => {
    var _a;
    const isUncontrolled = typeof isOpen === typeof undefined;
    const [currentState, setCurrentState] = useState(isOpen);
    const internalIsOpen = isUncontrolled ? currentState : isOpen;
    const internalSetOpen = isUncontrolled ? setCurrentState : onIsOpenChange;
    const getFloatingMiddlewares = () => {
        const middlewares = [
            offset({ mainAxis: removeParentGap ? 2 : 8 }),
            shift({
                limiter: limitShift(),
            }),
        ];
        if (persistOnClick) {
            middlewares.push(hide());
        }
        if (isFlippable) {
            middlewares.push(flip());
        }
        return middlewares;
    };
    const { x, y, refs, strategy, context, middlewareData } = useFloating({
        open: internalIsOpen,
        onOpenChange: internalSetOpen,
        placement: direction,
        middleware: getFloatingMiddlewares(),
        whileElementsMounted: autoUpdate,
    });
    const { isMounted, styles } = useTransitionStyles(context);
    const dismiss = useDismiss(context);
    const click = useClick(context, { enabled: persistOnClick });
    const hover = useHover(context, {
        handleClose: safePolygon(),
        enabled: !persistOnClick,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, click, hover]);
    return (jsxs("div", { className: "inline-block overflow-hidden", children: [jsx("div", Object.assign({ ref: refs.setReference }, getReferenceProps(), { children: parentElement === 'icon' ? (jsx(EmptyInfoCircleIcon, { className: `text-body text-gray-600 hover:text-black ${internalIsOpen ? '!text-black' : ''}` })) : (parentElement) })), isMounted && (jsx(FloatingPortal, { children: jsx("div", Object.assign({ ref: refs.setFloating, style: Object.assign({ position: strategy, top: y !== null && y !== void 0 ? y : 0, left: x !== null && x !== void 0 ? x : 0, width: 'min(max-content, 100vw)' }, styles) }, getFloatingProps(), { className: `z-floating max-w-sm rounded-md bg-black break-words text-white
						${persistOnClick && ((_a = middlewareData === null || middlewareData === void 0 ? void 0 : middlewareData.hide) === null || _a === void 0 ? void 0 : _a.referenceHidden) ? 'hidden' : ''}
						${variant === 'no-padding' ? '' : 'p-4'} drop-shadow-tooltip ${className}`, children: typeof childElement === 'object' && childElement && 'header' in childElement ? (jsxs("div", { className: "flex flex-col gap-2", children: [jsx("p", { className: "text-paragraph font-bold", children: childElement.header }), jsx("p", { className: "text-caption1 font-normal", children: childElement.description })] })) : (childElement) })) }))] }));
};

export { Tooltip };
